import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { useLanguage } from "@/contexts/LanguageContext";
import { Play, Image as ImageIcon, Sparkles } from "lucide-react";

interface ExampleItem {
  type: "video" | "image";
  src: string;
  label: string;
  model: string;
}

const examples: ExampleItem[] = [
  // Videos
  {
    type: "video",
    src: "/example/veo3exam1.mp4",
    label: "Veo 3",
    model: "Google Veo 3",
  },
  {
    type: "video",
    src: "/example/example sora1.mp4",
    label: "Seedance 2.0",
    model: "Seedance 2.0",
  },
  {
    type: "video",
    src: "/example/example sora2.mp4",
    label: "Seedance 2.0",
    model: "Seedance 2.0",
  },
  {
    type: "video",
    src: "/example/example sora3.mp4",
    label: "Seedance 2.0",
    model: "Seedance 2.0",
  },
  // Images
  {
    type: "image",
    src: "/example/seedream4.5.jpg",
    label: "Seedream 4.5",
    model: "Seedream 4.5",
  },
  {
    type: "image",
    src: "/example/seedream4.5-2.jpeg",
    label: "Seedream 4.5",
    model: "Seedream 4.5",
  },
  {
    type: "image",
    src: "/example/seedream4.5-3.jpg",
    label: "Seedream 4.5",
    model: "Seedream 4.5",
  },
  {
    type: "image",
    src: "/example/seedream4.5-2-4.jpg",
    label: "Seedream 4.5",
    model: "Seedream 4.5",
  },
  {
    type: "image",
    src: "/example/google nano banana.png",
    label: "Gemini Banana",
    model: "Gemini Banana",
  },
];

const ExampleShowcase = () => {
  const { t } = useLanguage();
  const [playingVideo, setPlayingVideo] = useState<string | null>(null);

  const videos = examples.filter((e) => e.type === "video");
  const images = examples.filter((e) => e.type === "image");

  return (
    <section className="py-24 relative overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-purple-500/5 to-transparent pointer-events-none" />

      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Sparkles className="h-6 w-6 text-purple-400" />
            <span className="text-purple-400 font-semibold">
              {t("ตัวอย่างผลงาน", "Example Gallery")}
            </span>
          </div>
          <h2 className="text-3xl md:text-5xl font-heading font-bold mb-4">
            {t("ดูผลงาน", "See What")}{" "}
            <span className="text-gradient">{t("AI สร้างได้", "AI Creates")}</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {t(
              "ตัวอย่างวิดีโอและภาพที่สร้างจากโมเดล AI ชั้นนำ",
              "Sample videos and images generated by leading AI models"
            )}
          </p>
        </div>

        {/* Videos Section - 9:16 Portrait */}
        <div className="mb-12">
          <h3 className="text-xl font-semibold mb-6 flex items-center gap-2">
            <Play className="h-5 w-5 text-purple-400" />
            {t("วิดีโอตัวอย่าง", "Sample Videos")}
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {videos.map((video, index) => (
              <Card
                key={index}
                variant="glass"
                className="group overflow-hidden hover:border-purple-500/50 transition-all duration-300"
              >
                <CardContent className="p-0 relative">
                  <div className="aspect-[9/16] relative bg-black/50">
                    <video
                      src={video.src}
                      className="w-full h-full object-cover"
                      loop
                      muted
                      playsInline
                      autoPlay
                      onMouseEnter={(e) => {
                        e.currentTarget.play();
                        setPlayingVideo(video.src);
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.currentTime = 0;
                        setPlayingVideo(null);
                      }}
                    />
                    {playingVideo !== video.src && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/10 transition-all">
                        <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center group-hover:scale-110 transition-transform">
                          <Play className="h-6 w-6 text-white ml-1" />
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="p-3">
                    <span className="inline-block px-2 py-1 text-xs font-medium rounded-full bg-purple-500/20 text-purple-300">
                      {video.model}
                    </span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Images Section - 9:16 Portrait */}
        <div>
          <h3 className="text-xl font-semibold mb-6 flex items-center gap-2">
            <ImageIcon className="h-5 w-5 text-cyan-400" />
            {t("ภาพตัวอย่าง", "Sample Images")}
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {images.map((image, index) => (
              <Card
                key={index}
                variant="glass"
                className="group overflow-hidden hover:border-cyan-500/50 transition-all duration-300"
              >
                <CardContent className="p-0 relative">
                  <div className="aspect-[9/16] relative overflow-hidden">
                    <img
                      src={image.src}
                      alt={image.label}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-3">
                    <span className="inline-block px-2 py-1 text-xs font-medium rounded-full bg-cyan-500/20 text-cyan-300">
                      {image.model}
                    </span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ExampleShowcase;
